if (!customElements.get('quick-add-bulk')) {
  customElements.define(
    'quick-add-bulk',
    class QuickAddBulk extends BulkAdd {
      constructor() {
        super();
        this.quantity = this.querySelector('quantity-input');

        const debouncedOnChange = debounce((event) => {
          if (!this._addFromButton) return;
          this._addFromButton = false;
          if (parseInt(event.target.value) === 0) {
            this.startQueue(event.target.dataset.index, parseInt(event.target.value));
          } else {
            this.validateQuantity(event);
          }
        }, ON_CHANGE_DEBOUNCE_TIMER);

        this.addEventListener('change', debouncedOnChange.bind(this));
        this.listenForActiveInput();
        this.listenForKeydown();
        this.lastActiveInputId = null;
      }

      connectedCallback() {
        this.addEventListener('click', (e) => {
          if (e.target.closest('.quick-add-bulk__add-button')) {
            const input = this.querySelector('quantity-input input');
            if (input) {
              this._addFromButton = true;
              input.dispatchEvent(new Event('change', { bubbles: true }));
              const addBtn = this.querySelector('.quick-add-bulk__add-button');
              if (addBtn) {
                const addedLabel = addBtn.dataset.addedLabel || 'Added';
                const cartLabel = addBtn.dataset.addToCartLabel || 'Add to cart';
                addBtn.textContent = addedLabel;
                addBtn.classList.add('quick-add-bulk__add-button--added');
                if (this._addedTimeout) clearTimeout(this._addedTimeout);
                this._addedTimeout = setTimeout(() => {
                  addBtn.textContent = cartLabel;
                  addBtn.classList.remove('quick-add-bulk__add-button--added');
                  this._addedTimeout = null;
                }, 2500);
              }
            }
          }
        });
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (
            event.source === 'quick-add' ||
            (event.cartData.items && !event.cartData.items.some((item) => item.id === parseInt(this.dataset.index))) ||
            (event.cartData.variant_id && !(event.cartData.variant_id === parseInt(this.dataset.index)))
          ) {
            return;
          }
          this.onCartUpdate().then(() => {
            this.listenForActiveInput();
            this.listenForKeydown();
          });
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
        if (this._addedTimeout) {
          clearTimeout(this._addedTimeout);
          this._addedTimeout = null;
        }
      }

      get input() {
        return this.querySelector('quantity-input input');
      }

      selectProgressBar() {
        return this.querySelector('.progress-bar-container');
      }

      listenForActiveInput() {
        if (!this.classList.contains('hidden')) {
          this.input?.addEventListener('focusin', (event) => event.target.select());
        }
        this.isEnterPressed = false;
      }

      listenForKeydown() {
        this.input?.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            this.input?.blur();
            this.isEnterPressed = true;
          }
        });
      }

      cleanErrorMessageOnType(event) {
        event.target.addEventListener(
          'keypress',
          () => {
            event.target.setCustomValidity('');
          },
          { once: true }
        );
      }

      get sectionId() {
        if (!this._sectionId) {
          this._sectionId = this.closest('.collection-quick-add-bulk').dataset.id;
        }

        return this._sectionId;
      }

      onCartUpdate() {
        return new Promise((resolve, reject) => {
          fetch(`${this.getSectionsUrl()}?section_id=${this.sectionId}`)
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const sourceQty = html.querySelector(`#quick-add-bulk-${this.dataset.index}-${this.sectionId}`);
              if (sourceQty) {
                this.innerHTML = sourceQty.innerHTML;
              }
              resolve();
            })
            .catch((e) => {
              console.error(e);
              reject(e);
            });
        });
      }

      getSectionsUrl() {
        const pageParams = new URLSearchParams(window.location.search);
        const pageNumber = decodeURIComponent(pageParams.get('page') || '');

        return `${window.location.pathname}${pageNumber ? `?page=${pageNumber}` : ''}`;
      }

      updateMultipleQty(items) {
        this.selectProgressBar().classList.remove('hidden');

        const ids = Object.keys(items);
        const body = JSON.stringify({
          updates: items,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: this.getSectionsUrl(),
        });

        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => {
            return response.text();
          })
          .then((state) => {
            const parsedState = JSON.parse(state);
            this.renderSections(parsedState, ids);
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData: parsedState });
          })
          .catch(() => {
            // Commented out for now and will be fixed when BE issue is done https://github.com/Shopify/shopify/issues/440605
            // e.target.setCustomValidity(error);
            // e.target.reportValidity();
            // this.resetQuantityInput(ids[index]);
            // this.selectProgressBar().classList.add('hidden');
            // e.target.select();
            // this.cleanErrorMessageOnType(e);
          })
          .finally(() => {
            this.selectProgressBar().classList.add('hidden');
            this.setRequestStarted(false);
          });
      }

      getSectionsToRender() {
        return [
          {
            id: `quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
            section: this.sectionId,
            selector: `#quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          {
            id: 'CartDrawer',
            selector: '.drawer__inner',
            section: 'cart-drawer',
          },
        ];
      }

      renderSections(parsedState, ids) {
        const intersection = this.queue.filter((element) => ids.includes(element.id));
        if (intersection.length !== 0) return;
        const cartIconSection = this.getSectionsToRender().find((s) => s.section === 'cart-icon-bubble');
        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.getElementById(section.id);
          if (!sectionElement) return;
          if (section.section === 'cart-drawer') {
            sectionElement.closest('cart-drawer')?.classList.toggle('is-empty', parsedState.items.length.length === 0);
          }
          let sectionHtml = parsedState.sections && parsedState.sections[section.section];
          if (section.section === 'cart-icon-bubble' && !sectionHtml) {
            const path = this.getSectionsUrl();
            const sectionUrl = `${window.location.origin}${path}${path.indexOf('?') >= 0 ? '&' : '?'}section_id=cart-icon-bubble`;
            fetch(sectionUrl)
              .then((r) => r.text())
              .then((html) => {
                const doc = new DOMParser().parseFromString(html, 'text/html');
                const sectionEl = doc.querySelector('.shopify-section') || doc.body;
                if (sectionEl && cartIconSection) {
                  const elementToReplace =
                    document.getElementById(cartIconSection.id)?.querySelector(cartIconSection.selector) ||
                    document.getElementById(cartIconSection.id);
                  if (elementToReplace) {
                    elementToReplace.innerHTML = sectionEl.innerHTML;
                  }
                }
              })
              .catch(() => {});
            return;
          }
          if (sectionHtml) {
            const elementToReplace =
              sectionElement.querySelector(section.selector) || sectionElement;
            try {
              elementToReplace.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
            } catch (e) {}
          }
        });

        if (this.isEnterPressed) {
          this.querySelector(`#Quantity-${this.lastActiveInputId}`).select();
        }

        this.listenForActiveInput();
        this.listenForKeydown();
      }
    }
  );
}
